import React, { useEffect, useState } from "react";
import { useKeenSlider } from "keen-slider/react";

declare global {
  interface Window {
    qgiv?: {
      init: () => void;
    };
  }
}

const countryContent = {
  Default: null,
  Pakistan: {
    images: [
      "/assets/IMG_0329.JPG",
      "/assets/IMG_0330.JPG",
      "/assets/IMG_0331.JPG",
      "/assets/IMG_0332.JPG",
      "/assets/IMG_0333.JPG",
      "/assets/IMG_0335.JPG",
      "/assets/IMG_0336.JPG"
    ],
    partner: "FRDP",
    merch: "/shop/pakistan"
  }
} as const;

type Country = keyof typeof countryContent;

const Donate: React.FC = () => {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<Country>("Default");
  const [sliderRef] = useKeenSlider<HTMLDivElement>({
    loop: true,
    slides: { perView: 1, spacing: 15 },
    created(slider) {
      setInterval(() => {
        slider.next();
      }, 3000); // Auto slide every 3 seconds
    },
  });

  useEffect(() => {
    const scriptId = "qgiv-embedjs";

    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.src = "https://secure.qgiv.com/resources/core/js/embed.js";
      script.id = scriptId;
      script.async = true;
      script.onload = () => setScriptLoaded(true);
      script.onerror = () => {
        console.error("Failed to load Qgiv script.");
        setError(true);
      };
      document.body.appendChild(script);
    } else {
      setScriptLoaded(true);
    }

    return () => {
      // Prevent script removal if other pages also use it
      if (document.getElementById(scriptId)) {
        console.log("Qgiv script remains for reuse.");
      }
    };
  }, []);

  useEffect(() => {
    if (scriptLoaded) {
      const container = document.querySelector(".qgiv-embed-container");
      if (container) {
        container.innerHTML = "";
      }
    }
  }, [selectedCountry, scriptLoaded]);

  return (
    <div className="min-h-screen bg-[#FAF0E6] px-6 py-16 flex flex-col items-center font-alice">
      <div className="max-w-3xl w-full text-center">
        <h1 className="text-5xl font-bold text-gray-800 font-alice">Support Wasaela Foundation</h1>
        <h2 className="text-xl text-gray-700 mt-2">
          Every dollar brings hope, healing, and dignity to someone in need.
        </h2>
        <p className="text-lg text-gray-600 mt-4">
          Your generosity fuels our mission to provide clean water, safe housing, healthcare, and education
          to underserved communities. Every contribution brings hope and a step toward self-reliance.
        </p>

        <div className="mt-8 bg-[#DAF0F7] p-4 rounded-lg shadow text-gray-800">
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Honor Someone Special</h3>
          <p>
            Want to give in honor of someone special? You can dedicate your gift as a tribute through the form below.
          </p>
        </div>

        <div className="mt-8 bg-white bg-opacity-90 p-6 rounded-xl shadow-lg border border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-800 text-center">Why Donate?</h2>
          <ul className="list-disc list-inside text-gray-700 mt-4">
            <li>Provide clean and safe drinking water to those in need.</li>
            <li>Support mobile clinics and healthcare services.</li>
            <li>Fund safe housing projects for vulnerable families.</li>
            <li>Empower communities with education and vocational training.</li>
          </ul>
          <p className="mt-4 text-gray-600">
            <span className="font-bold">100% of your donation</span> goes directly to our projects, ensuring impactful change.
          </p>
        </div>

        <h2 className="text-3xl font-semibold text-gray-800 mt-10 font-alice">Make a Difference Today</h2>
        <p className="text-lg text-gray-700 mt-2">
          Your support transforms lives. Join us in creating lasting impact.
        </p>

        <div className="mt-10 bg-white p-6 rounded-xl shadow-md border border-gray-200 max-w-md mx-auto hover:shadow-lg hover:ring-1 hover:ring-[#9CAF88] transition duration-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-2 uppercase tracking-wide">Select a Country Fund</h3>
          <p className="text-sm text-gray-500 mb-3">Your donation will support work in this country.</p>
          <select
            id="country"
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value as Country)}
            className="appearance-none w-full px-5 py-3 border border-gray-300 rounded-full text-gray-800 font-medium shadow-sm bg-white hover:border-[#9CAF88] focus:ring-2 focus:ring-[#9CAF88] focus:outline-none transition duration-200"
          >
            <option value="Default">🌍 General Donation</option>
            <option value="Pakistan">🇵🇰 Pakistan</option>
          </select>
        </div>

        {selectedCountry !== "Default" && countryContent[selectedCountry] && (
          <div className="mt-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-2">What We’ve Built with Our Partner: FRDP 🇵🇰</h3>
            <div className="flex flex-col md:flex-row gap-6">
              <div className="md:w-1/2">
                <h4 className="text-lg font-medium text-gray-800 mb-2 text-left">Water Wells Built</h4>
                <p className="text-gray-600 mb-4 text-left">
                  Families across Pakistan now enjoy clean, safe water thanks to your support.
                </p>
                <div ref={sliderRef} className="keen-slider">
                  {countryContent.Pakistan.images.map((img, idx) => (
                    <div key={idx} className="keen-slider__slide flex justify-center">
                      <img
                        src={img}
                        alt={`Project ${idx + 1}`}
                        className="w-full h-auto object-cover rounded-xl shadow-md"
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="md:w-1/2 flex flex-col items-center justify-center">
                <h4 className="text-lg font-medium text-gray-800 mb-2 text-left">Orphan Sponsorship Video</h4>
                <p className="text-gray-600 mb-4 text-left">
                  Watch how our orphan sponsorship program brings hope to vulnerable children.
                </p>
                <video
                  controls
                  src="/assets/orphan-video.mp4"
                  className="w-full h-auto rounded-xl shadow-md"
                />
              </div>
            </div>
          </div>
        )}

        <hr className="border-t border-gray-300 my-12 w-full max-w-lg mx-auto" />

        <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200 mt-12">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4 text-center">
            {selectedCountry === "Pakistan"
              ? "Donate to the Pakistan Relief Fund"
              : "Donate to Wasaela Foundation"}
          </h2>
          {selectedCountry === "Pakistan" ? (
            <iframe
              title="Pakistan Qgiv Donation Form"
              src="https://secure.qgiv.com/for/pakistanfund/embed/79679/"
              width="630"
              height="800"
              className="w-full border-none"
            ></iframe>
          ) : (
            <iframe
              title="Wasaela General Donation Form"
              src="https://secure.qgiv.com/for/wasaelafoundation/embed/78937/"
              width="630"
              height="800"
              className="w-full border-none"
            ></iframe>
          )}
          <p className="text-sm text-gray-500 mt-4 text-center">
            This form is secure and powered by Qgiv. Your information is encrypted and protected.
          </p>
          <p className="text-sm text-gray-500 mt-1 text-center">
            You’ll receive a confirmation email after submitting your gift.
          </p>
          <p className="text-md text-gray-600 mt-8 text-center">
            Thank you for standing with us. Your generosity makes sustainable impact possible.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Donate;